import React, { memo, useEffect, useState } from "react";
import { Handle, Position } from "reactflow";
import "./Stage.scss";
import { INIT_WORKFLOW_STAGE, WorkFlowStage } from "../../models/Workflow";
import Filter from "../Filter/Filter";
import { FilterOptionSchema } from "../Filter/filter.data";
import { useWorkFlowContext } from "../../context/workflow-context";
import { useLocation } from "react-router-dom";

interface StageProps {
  data: WorkFlowStage;
  isConnectable: boolean;
}

const Stage: React.FC<StageProps> = ({ data, isConnectable }) => {
  const [editMode, setEditMode] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [details, setDetails] = useState<WorkFlowStage>(INIT_WORKFLOW_STAGE);
  const [oldDetails, setOldDetails] =
    useState<WorkFlowStage>(INIT_WORKFLOW_STAGE);
  const { updateNodeDetails, nodeDetails, teamList } = useWorkFlowContext();
  const [mode, setMode] = useState("");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.indexOf("edit") > -1) {
      setMode("edit");
      setEditMode(true);
    } else if (location.pathname.indexOf("create") > -1) {
      setMode("create");
      setEditMode(true);
    } else {
      setMode("view");
    }
  }, [location]);

  useEffect(() => {
    setDetails(nodeDetails[data.id]);
    setShowDetail(nodeDetails[data.id].new);
  }, [nodeDetails]);

  const updateDetails = (key: string, value: string | FilterOptionSchema) => {
    setDetails((old) => {
      return {
        ...old,
        [key]: value,
      };
    });
  };

  const updateSubtask = (value: string, index: number) => {
    setDetails((old) => {
      const tasks = [...old.subtasks];
      tasks[index] = { ...tasks[index], name: value };
      return {
        ...old,
        subtasks: tasks,
      };
    });
  };

  const startEdit = () => {
    setShowDetail(true);
    setOldDetails(details);
    setDetails((old) => {
      const subtasks = old.subtasks ? [...old.subtasks] : [];
      if (subtasks.length === 0) {
        subtasks.push({ id: -1, name: "" });
      }
      return { ...old, subtasks };
    });
  };

  const cancelEdit = () => {
    setShowDetail(false);
    if (editMode) {
      setDetails(oldDetails);
    }
  };

  const addSubtasks = () => {
    setDetails((old) => {
      const subtasks = old.subtasks ? [...old.subtasks] : [];
      subtasks.push({ id: -1, name: "" });
      return { ...old, subtasks };
    });
  };

  const saveStageDetails = () => {
    setEditMode(false);
    updateNodeDetails({
      ...details,
      new: false,
      subtasks: details.subtasks.filter((task) => task.name !== ""),
    });
  };

  const cardClicked = () => {
    if (mode === "view" && !showDetail) {
      setShowDetail(true);
    }
  };

  return (
    <>
      <div
        className={`stage-container p-12 flex-column justify-content-space-between ${
          !details.new && !editMode && "selected-stage-container"
        }`}
        style={{
          height: `${showDetail ? "unset" : "150px"}`,
          gap: `${showDetail ? "12px" : "0px"}`,
        }}
        onClick={cardClicked}
      >
        {showDetail ? (
          <>
            <div className="flex-row justify-content-space-between align-items-center gap-12">
              <div
                className="cancel-btn xetgo-font-tag p-8"
                onClick={cancelEdit}
              >
                Cancel
              </div>
              {editMode && (
                <div
                  className="save-btn xetgo-font-tag p-8"
                  onClick={saveStageDetails}
                >
                  Save
                </div>
              )}
            </div>
            <div className="flex-column gap-6">
              <p
                className="xetgo-font-tag bolder"
                style={{
                  color: `${editMode ? "#333333" : "#33333333"}`,
                }}
              >
                Add Title
              </p>
              <input
                disabled={!editMode}
                placeholder="Packaging Team Review"
                className="placeholder-input p-8 xetgo-font-tag"
                onChange={(e) => updateDetails("title", e.target.value)}
                value={details.title}
              />
            </div>
            <div className="flex-column gap-6">
              <p
                className="xetgo-font-tag bolder"
                style={{
                  color: `${editMode ? "#333333" : "#33333333"}`,
                }}
              >
                Add Description
              </p>
              {editMode ? (
                <textarea
                  rows={2}
                  placeholder="Analysis of the components"
                  className="placeholder-input p-8 xetgo-font-tag"
                  onChange={(e) => updateDetails("description", e.target.value)}
                  value={details.description}
                />
              ) : (
                <p className="placeholder-input p-8 xetgo-font-tag">
                  {details.description}
                </p>
              )}
            </div>
            {editMode ? (
              <div className="flex-column gap-6">
                <div className="flex-row gap-4 align-items-center">
                  <img
                    src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250227055059-user-plus+%281%29.svg"
                    alt="add-team"
                    width={12}
                    height={12}
                  />
                  <p
                    className="xetgo-font-tag bold"
                    style={{ color: "#333333" }}
                  >
                    Assign Team
                  </p>
                </div>
                <Filter
                  option={{
                    key: "teams",
                    name: "Teams",
                  }}
                  optionList={teamList || []}
                  multipleSelection={false}
                  onSelection={(val, _option) => updateDetails("team", val[0])}
                  preSelected={details.team ? [details.team] : []}
                  mode="showNames"
                  showPlaceholder={true}
                  direction="left"
                  addNew={false}
                  classname="team-filter-input"
                />
              </div>
            ) : (
              <div className="flex-column gap-6">
                <div className="flex-row gap-4 align-items-center">
                  <img
                    src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250225112230-user-plus.svg"
                    alt="add-team"
                    width={12}
                    height={12}
                  />
                  <p className="xetgo-font-tag bold disabled-title">
                    Assign Team
                  </p>
                </div>
                <input
                  disabled
                  placeholder="Search Team: Enter Name"
                  className="placeholder-input p-8 xetgo-font-tag"
                  value={details.team.name}
                />
              </div>
            )}
            <div className="flex-column gap-6">
              <p
                className="xetgo-font-tag bold disabled-title"
                style={{
                  color: `${editMode ? "#333333" : "#33333333"}`,
                }}
              >
                SLA
              </p>
              <div className="position-relative gap-6">
                <input
                  disabled={!editMode}
                  type="number"
                  placeholder="Enter number of days"
                  className="placeholder-input p-8 xetgo-font-tag full-width"
                  onChange={(e) => updateDetails("sla", e.target.value)}
                  value={details.sla}
                />
                <div
                  className="flex-row gap-6"
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "8px",
                    color: `${editMode ? "#2960EC" : "#33333333"}`,
                  }}
                >
                  <p className="xetgo-font-tag">|</p>
                  <p className="xetgo-font-tag">Days</p>
                </div>
              </div>
            </div>
            <div
              className="full-width"
              style={{ background: "#C6C6C6", height: "1px" }}
            ></div>
            <div className="flex-column gap-12">
              <p
                className="xetgo-font-tag bold"
                style={{
                  color: `${editMode ? "#333333" : "#33333333"}`,
                }}
              >
                Checklist
              </p>

              {details.subtasks ? (
                <div className="flex-column gap-16">
                  {details.subtasks.map((task, index) => (
                    <input
                      key={`task-name-${index}`}
                      disabled={!editMode}
                      placeholder="Task Name"
                      className="placeholder-input p-8 xetgo-font-tag"
                      value={task.name}
                      onChange={(e) => updateSubtask(e.target.value, index)}
                    />
                  ))}
                </div>
              ) : (
                <input
                  disabled
                  placeholder="Task Name"
                  className="placeholder-input p-8 xetgo-font-tag"
                />
              )}
              {editMode && (
                <div className="flex-row gap-6 align-items-center justify-content-flex-end">
                  <img
                    src={
                      editMode
                        ? "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250225113102-plus-square.svg"
                        : "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250228090748-plus-square+%282%29.svg"
                    }
                    alt="add-subtask"
                    height={12}
                    width={12}
                  ></img>
                  <p
                    className="xetgo-font-tag bold cursor-pointer"
                    style={{ color: `${editMode ? "#333333" : "#33333333"}` }}
                    onClick={addSubtasks}
                  >
                    Add Item
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-row align-items-center justify-content-space-between">
              <p
                className="xetgo-font-button bolder"
                style={{ color: "#333333" }}
              >
                {details.title}
              </p>
              <div className="flex-row gap-6 align-items-center justify-contentflex-end">
                {mode !== "view" && (
                  <div className="flex-row align-items-center justify-content-center action-container-mini">
                    <img
                      src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250225110757-pen-square.svg"
                      alt="edit"
                      height={12}
                      onClick={startEdit}
                    />
                  </div>
                )}
              </div>
            </div>
            <p
              className="xetgo-font-tag ellipsis"
              style={{ color: "#4F5763", height: "32px", width: "100%" }}
            >
              {details.description}
            </p>
            <div className="flex-row align-items-center gap-4">
              <img
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250227055059-user-plus+%281%29.svg"
                alt="team"
                height={12}
              />
              <p
                className="flex-row xetgo-font-tag"
                style={{ color: "#333333" }}
              >
                {details.team?.name}
              </p>
            </div>
            <div className="flex-row align-items-center gap-4">
              <img
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250228064659-calendar-days.svg"
                alt="sla"
                height={12}
              />
              <p
                className="flex-row xetgo-font-tag"
                style={{ color: "#333333" }}
              >
                {`${
                  details.sla ? "SLA - " + details.sla + " days" : "SLA - None"
                }`}
              </p>
            </div>
          </>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Left}
        id="a"
        onConnect={(params) => console.log("handle onConnect", params)}
        isConnectable={isConnectable}
        className={`${mode === "view" && "hidden-handle"}`}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="b"
        onConnect={(params) => console.log("handle onConnect", params)}
        isConnectable={isConnectable}
        className={`${mode === "view" && "hidden-handle"}`}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="c"
        onConnect={(params) => console.log("handle onConnect", params)}
        isConnectable={isConnectable}
        className={`${mode === "view" && "hidden-handle"}`}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="d"
        onConnect={(params) => console.log("handle onConnect", params)}
        isConnectable={isConnectable}
        className={`${mode === "view" && "hidden-handle"}`}
      />
    </>
  );
};

export default Stage;
